import { useState } from "react";
import { useRenameScenario } from "@/api/queries";
import { errorText } from "@/components/ErrorPanel";
import type { Scenario } from "@/generated/backend/v1/plan";
import type { Family } from "@/generated/core/v1/family";
import { scenarioTitle, TITLE_MAX } from "@/plan-logic/decisions/title";

/**
 * A saved what-if's title with an inline rename: the pencil opens a text field; Enter or leaving
 * it saves (trimmed; a blank or unchanged title keeps the old one), Escape cancels.
 */
export function ScenarioTitle({
  scenario,
  family,
  as: Tag = "b",
}: {
  scenario: Scenario;
  family?: Family;
  as?: "b" | "h3";
}) {
  const rename = useRenameScenario();
  const title = scenarioTitle(scenario, family);
  const [draft, setDraft] = useState<string | null>(null);

  const save = () => {
    if (draft === null) return;
    const next = draft.trim();
    setDraft(null);
    if (!next || next === title) return;
    rename.mutate({ id: scenario.uuid, title: next.slice(0, TITLE_MAX) });
  };

  if (draft !== null) {
    return (
      <span className="dz-stitle">
        <input
          className="dz-stitle-in"
          value={draft}
          maxLength={TITLE_MAX}
          aria-label="What-if title"
          autoFocus
          onChange={(e) => setDraft(e.target.value)}
          onBlur={save}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              save();
            } else if (e.key === "Escape") {
              e.preventDefault();
              e.stopPropagation();
              setDraft(null);
            }
          }}
          data-testid="scenario-title-input"
        />
      </span>
    );
  }
  return (
    <span className="dz-stitle">
      <Tag data-testid="scenario-title">{rename.isPending ? rename.variables.title : title}</Tag>
      <button
        type="button"
        className="dz-pencil"
        onClick={() => setDraft(title)}
        aria-label={`Rename ${title}`}
        title="Rename"
        data-testid="scenario-rename"
      >
        <span aria-hidden="true">✎</span>
      </button>
      {rename.isError && (
        <span className="wp-form-err" role="alert">
          {errorText(rename.error)}
        </span>
      )}
    </span>
  );
}
